import React, { useState } from 'react';
import {
  App,
  Card,
  Table,
  Select,
  Switch,
  Typography,
  Space,
  Skeleton,
  Alert,
  Input,
  Button,
  Tag,
  Tooltip,
} from 'antd';
import {
  PrinterOutlined,
  CloudOutlined,
  ShopOutlined,
  ArrowLeftOutlined,
  KeyOutlined,
  CopyOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useResponsive } from '../hooks/useResponsive';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import {
  useGetPrinterConfigurationsQuery,
  useUpdatePrinterConfigurationMutation,
  useFlashPrinterLogoMutation,
  PrinterConfig,
} from '../services/printersApi';
import {
  useGetProductionCentersQuery,
  useUpdateProductionCenterMutation,
  ProductionCenter,
} from '../services/productionCenterApi';
import {
  useGetBoolSettingQuery,
  useGetSettingQuery,
  useUpdateSettingMutation,
} from '../services/settingsApi';

const { Title, Text, Paragraph } = Typography;

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

const ApiKeySection: React.FC = () => {
  const { message } = App.useApp();
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchApiKey = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiBaseUrl}/cloud-print-jobs/api-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApiKey(data.apiKey || '');
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const generateApiKey = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiBaseUrl}/cloud-print-jobs/api-key/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setApiKey(data.apiKey || '');
        message.success('API Key generada');
      } else {
        message.error('Error al generar API Key');
      }
    } catch {
      message.error('Error de conexion');
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    message.success('API Key copiada al portapapeles');
  };

  React.useEffect(() => { fetchApiKey(); }, []);

  if (loading) return <Skeleton.Input active style={{ width: 300 }} />;

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {apiKey ? (
        <Space>
          <Input.Password
            value={apiKey}
            readOnly
            style={{ width: 350, fontFamily: 'monospace' }}
          />
          <Button icon={<CopyOutlined />} onClick={copyToClipboard}>Copiar</Button>
          <Button icon={<ReloadOutlined />} onClick={generateApiKey} danger>Regenerar</Button>
        </Space>
      ) : (
        <Button type="primary" icon={<KeyOutlined />} onClick={generateApiKey}>
          Generar API Key
        </Button>
      )}
    </Space>
  );
};

// Repo público de releases del Print Agent (el agente también se auto-actualiza desde ahí)
const RELEASES_REPO = 'jmanueels/massrestaurant-releases';

const isNewerVersion = (latest: string, current: string): boolean => {
  const a = latest.replace(/^v/i, '').split('.').map(Number);
  const b = current.replace(/^v/i, '').split('.').map(Number);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
};

const AgentVersionSection: React.FC = () => {
  const { data: versionSetting } = useGetSettingQuery('PrintAgentVersion');
  const [latest, setLatest] = useState<{ version: string; url: string } | null>(null);

  React.useEffect(() => {
    // API pública de GitHub (soporta CORS); si falla, simplemente no avisamos
    fetch(`https://api.github.com/repos/${RELEASES_REPO}/releases/latest`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.tag_name) setLatest({ version: data.tag_name, url: data.html_url });
      })
      .catch(() => { /* sin red o sin releases: silencio */ });
  }, []);

  const installed = versionSetting?.value;
  if (!installed) return null;

  const updateAvailable = latest && isNewerVersion(latest.version, installed);

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <Text type="secondary">
        Print Agent instalado: <Text strong>v{installed}</Text>
      </Text>
      {updateAvailable && (
        <Alert
          type="warning"
          showIcon
          message={`Hay una versión nueva del Print Agent (${latest!.version})`}
          description={
            <>
              El agente se actualiza solo en las siguientes 24 horas. Si no quieres esperar,{' '}
              <a href={latest!.url} target="_blank" rel="noreferrer">
                descárgala aquí
              </a>{' '}
              y ejecútala en la computadora de caja.
            </>
          }
        />
      )}
    </Space>
  );
};

const PrinterSettings: React.FC = () => {
  const { message, modal } = App.useApp();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // Printer configurations
  const { data: printers, isLoading: loadingPrinters } = useGetPrinterConfigurationsQuery();
  const [updatePrinter, { isLoading: updatingPrinter }] = useUpdatePrinterConfigurationMutation();

  const handleMakeDefault = async (printer: PrinterConfig) => {
    try {
      await updatePrinter({ id: printer.id, isDefault: true }).unwrap();
      message.success(`${printer.name} es ahora la impresora predeterminada`);
    } catch {
      message.error('Error al marcar la impresora como predeterminada');
    }
  };

  const handleNvLogoToggle = async (printer: PrinterConfig, checked: boolean) => {
    try {
      await updatePrinter({ id: printer.id, printNvLogo: checked }).unwrap();
      message.success(checked
        ? `${printer.name}: los tickets imprimirán el logo grabado en la impresora`
        : `${printer.name}: logo desactivado`);
    } catch {
      message.error('Error al actualizar la configuracion de logo');
    }
  };

  const [flashLogo, { isLoading: flashingLogo }] = useFlashPrinterLogoMutation();

  const handleFlashLogo = (printer: PrinterConfig) => {
    modal.confirm({
      title: `Grabar logo en ${printer.name}`,
      content:
        'Se graba el logo del restaurante (el de Branding) en la memoria de la impresora. ' +
        'La impresora se reinicia sola al terminar; no lo repitas muchas veces al día, ' +
        'la memoria se desgasta. ¿Continuar?',
      okText: 'Grabar logo',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await flashLogo(printer.id).unwrap();
          message.success(
            `Logo enviado a ${printer.name}. La impresora se reiniciará en unos segundos; ` +
            'después activa el switch de Logo para que salga en los tickets.',
          );
        } catch (err: any) {
          message.error(err?.data?.message || 'Error al grabar el logo');
        }
      },
    });
  };

  // Production centers
  const { data: productionCenters, isLoading: loadingPCs } = useGetProductionCentersQuery();
  const [updateProductionCenter] = useUpdateProductionCenterMutation();

  // Cloud print setting
  const { data: cloudPrintSetting, isLoading: loadingCloudPrint } = useGetBoolSettingQuery('CloudPrintEnabled');
  const [updateSetting] = useUpdateSettingMutation();

  const handleCloudPrintToggle = async (checked: boolean) => {
    try {
      await updateSetting({ key: 'CloudPrintEnabled', value: String(checked) }).unwrap();
      message.success(checked ? 'Cloud Print activado' : 'Cloud Print desactivado');
    } catch {
      message.error('Error al actualizar la configuracion');
    }
  };

  const handleProductionCenterPrinterChange = async (pc: ProductionCenter, printerConfigurationId: string | undefined) => {
    try {
      await updateProductionCenter({
        id: pc.id,
        data: {
          name: pc.name,
          description: pc.description,
          printTicket: pc.printTicket,
          showOnScreen: pc.showOnScreen,
          printerConfigurationId: printerConfigurationId || undefined,
          isActive: pc.isActive,
          sortOrder: pc.sortOrder,
        },
      }).unwrap();
      message.success(`Impresora asignada a ${pc.name}`);
    } catch {
      message.error('Error al asignar impresora');
    }
  };

  // Estado reportado por el Print Agent (DLE EOT en red, spooler en USB)
  const statusPresentation: Record<string, { label: string; color: string }> = {
    ONLINE: { label: 'En línea', color: 'green' },
    PAPER_OUT: { label: 'Sin papel', color: 'red' },
    PAPER_LOW: { label: 'Poco papel', color: 'orange' },
    COVER_OPEN: { label: 'Tapa abierta', color: 'orange' },
    OFFLINE: { label: 'Sin conexión', color: 'red' },
    ERROR: { label: 'Error', color: 'red' },
    UNKNOWN: { label: 'Desconocido', color: 'default' },
  };

  // Las impresoras (alta/conexión) se administran desde el Config Tool del agente;
  // aquí se decide su comportamiento: predeterminada, logo y monitoreo.
  const printerColumns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Conexion',
      dataIndex: 'connectionType',
      key: 'connectionType',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Estado',
      dataIndex: 'lastStatus',
      key: 'lastStatus',
      render: (lastStatus: string | null | undefined, record: PrinterConfig) => {
        if (!lastStatus) return <Tag>Sin datos</Tag>;
        const s = statusPresentation[lastStatus] ?? statusPresentation.UNKNOWN;
        const tag = <Tag color={s.color}>{s.label}</Tag>;
        return record.lastStatusAt ? (
          <Tooltip title={`Reportado: ${formatInTimeZone(record.lastStatusAt, 'DD/MM/YYYY HH:mm')}`}>
            {tag}
          </Tooltip>
        ) : tag;
      },
    },
    {
      title: 'Default',
      dataIndex: 'isDefault',
      key: 'isDefault',
      render: (isDefault: boolean, record: PrinterConfig) =>
        isDefault ? (
          <Tag color="green">Predeterminada</Tag>
        ) : (
          <Button size="small" loading={updatingPrinter} onClick={() => handleMakeDefault(record)}>
            Hacer default
          </Button>
        ),
    },
    {
      title: 'Logo',
      dataIndex: 'printNvLogo',
      key: 'printNvLogo',
      render: (printNvLogo: boolean | undefined, record: PrinterConfig) => (
        <Space size="small">
          <Tooltip title="Imprime el logo grabado en la memoria de la impresora al inicio de cuentas y pre-cuentas.">
            <Switch
              size="small"
              checked={!!printNvLogo}
              onChange={(checked) => handleNvLogoToggle(record, checked)}
            />
          </Tooltip>
          <Tooltip title="Graba el logo del restaurante (Branding) en la memoria de la impresora. Solo se necesita una vez.">
            <Button size="small" loading={flashingLogo} onClick={() => handleFlashLogo(record)}>
              Grabar logo
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loadingPrinters || loadingPCs || loadingCloudPrint) {
    return (
      <div style={{ padding: isMobile ? 16 : 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 16 : 24, maxWidth: 900 }}>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/configuracion')}
        style={{ padding: 0, marginBottom: 8 }}
      >
        Volver a Configuración
      </Button>
      <Title level={3}>
        <PrinterOutlined /> Configuración de Impresoras
      </Title>

      {/* Section 1: Configured printers */}
      <Card
        title={<><PrinterOutlined /> Impresoras configuradas</>}
        style={{ marginBottom: 24 }}
      >
        {printers && printers.length > 0 ? (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              Las impresoras se dan de alta desde la herramienta de configuracion (Config Tool)
              del agente de impresion. La impresora <Text strong>predeterminada</Text> recibe
              las cuentas, pre-cuentas y cortes de caja (todo lo que no es comanda de un centro
              de produccion).
            </Paragraph>
            <Table
              dataSource={printers}
              columns={printerColumns}
              rowKey="id"
              pagination={false}
              size="small"
              scroll={isMobile ? { x: 700 } : undefined}
            />
          </>
        ) : (
          <Alert
            type="info"
            message="No hay impresoras configuradas"
            description="Las impresoras se configuran desde la herramienta de configuracion (Config Tool) del agente de impresion."
            showIcon
          />
        )}
      </Card>

      {/* Section 2: Production center -> printer mapping (only when cloud print is active) */}
      {cloudPrintSetting?.value && <Card
        title={<><ShopOutlined /> Centros de producción → Impresoras</>}
        style={{ marginBottom: 24 }}
      >
        {/* Sin filtrar por printTicket: la asignación de impresora sobrevive al toggle
            y ocultar el renglón mientras está apagado hacía imposible verla o corregirla. */}
        {productionCenters && productionCenters.length > 0 ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {productionCenters.map((pc) => (
              <div key={pc.id} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Text strong style={{ minWidth: 150 }}>
                  {pc.name}
                  {!pc.printTicket && <Tag style={{ marginLeft: 8 }}>Impresión apagada</Tag>}
                </Text>
                <Text type="secondary">→</Text>
                <Select
                  style={{ minWidth: 200, flex: 1, maxWidth: 300 }}
                  placeholder="Seleccionar impresora"
                  value={pc.printerConfigurationId || undefined}
                  allowClear
                  onChange={(value) => handleProductionCenterPrinterChange(pc, value)}
                  options={
                    printers?.map(p => ({ label: p.name, value: p.id })) ?? []
                  }
                />
              </div>
            ))}
          </Space>
        ) : (
          <Alert
            type="info"
            message="No hay centros de produccion"
            description="Crea centros de produccion desde el menu de administracion para asignarles impresoras."
            showIcon
          />
        )}
      </Card>}

      {/* Section 3: Cloud Print toggle */}
      <Card
        title={<><CloudOutlined /> Cloud Print</>}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Switch
              checked={cloudPrintSetting?.value ?? false}
              onChange={handleCloudPrintToggle}
            />
            <Text strong>
              {cloudPrintSetting?.value ? 'Activado' : 'Desactivado'}
            </Text>
          </div>
          <Paragraph type="secondary">
            Cuando esta activado, todas las impresiones se envian al servidor y son recogidas
            por el agente de impresion instalado en la computadora de caja. Esto permite imprimir
            desde cualquier dispositivo (celular, tablet) sin necesidad de conexion directa a la
            impresora.
          </Paragraph>
          {cloudPrintSetting?.value && <AgentVersionSection />}
        </Space>
      </Card>

      {/* Section 4: API Key (only when cloud print is active) */}
      {cloudPrintSetting?.value && (
        <Card
          title={<><KeyOutlined /> API Key del Print Agent</>}
          style={{ marginBottom: 24 }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Paragraph type="secondary">
              Esta clave se usa para conectar el Print Agent al servidor sin necesidad de usuario y contraseña.
              Copiala y pegala en la herramienta de configuracion del Print Agent.
            </Paragraph>
            <ApiKeySection />
          </Space>
        </Card>
      )}
    </div>
  );
};

export default PrinterSettings;
